import { useEffect, useRef, useState } from 'preact/hooks';
import { useSignalEffect } from '@preact/signals';
import { Hand, LocateFixed, Route as RouteIcon } from 'lucide-preact';
import { routeStore } from '../state/routeStore';
import { playbackProgress } from '../state/playback';
import type { Route, RouteDestination, RouteWaypoint } from '../backend/route';

/**
 * MapLibre is heavy (~220 KB gzipped). We dynamic-import it after first paint
 * so the StatusHero and ConnectionPill render immediately and the map slides
 * in once the chunk lands.
 */
type MapLibreModule = typeof import('maplibre-gl');

const TRAIL_SOURCE = 'as-trail';
const TRAIL_LAYER = 'as-trail-line';

type CameraMode = 'route' | 'current' | 'free';

type MapHandles = {
  map: import('maplibre-gl').Map;
  traveller: import('maplibre-gl').Marker | null;
  destination: import('maplibre-gl').Marker | null;
  start: import('maplibre-gl').Marker | null;
  finish: import('maplibre-gl').Marker | null;
  hasFitInitialBounds: boolean;
  lastFollowedWaypointKey: string | null;
  lastFittedRouteKey: string | null;
};

export function LiveMap() {
  const containerRef = useRef<HTMLDivElement>(null);
  const handlesRef = useRef<MapHandles | null>(null);
  const moduleRef = useRef<MapLibreModule | null>(null);
  const userSelectedCameraModeRef = useRef(false);
  const [cameraMode, setCameraMode] = useState<CameraMode>('route');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      import('maplibre-gl'),
      import('maplibre-gl/dist/maplibre-gl.css'),
      import('../lib/mapStyle'),
    ]).then(([mod, _css, styleModule]) => {
      if (cancelled || !containerRef.current) return;
      const maplibregl = mod.default ?? (mod as unknown as MapLibreModule);
      moduleRef.current = maplibregl;
      // Initialise the map with its FINAL bounds already applied so the very
      // first rendered frame shows the route in place — no centre/zoom jump
      // afterwards. App.tsx guarantees at least one waypoint here.
      const initialRoute = routeStore.value;
      const initialView = computeInitialView(maplibregl, initialRoute);
      const map = new maplibregl.Map({
        container: containerRef.current,
        style: styleModule.getMapStyle(),
        ...initialView,
        attributionControl: { compact: true },
      });
      // Bottom-left so the controls don't fight the floating download chip on
       // the right. CSS in tokens.css lifts the corner up clear of the
       // collapsed drawer height.
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-left');
      const markFreePan = (event: { originalEvent?: Event }) => {
        if (!event.originalEvent) return;
        userSelectedCameraModeRef.current = true;
        setCameraMode('free');
      };
      map.on('dragstart', markFreePan);
      map.on('zoomstart', markFreePan);
      map.on('rotatestart', markFreePan);
      map.on('pitchstart', markFreePan);
      map.on('load', () => {
        if (!map.getSource(TRAIL_SOURCE)) {
          map.addSource(TRAIL_SOURCE, {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] },
          });
          map.addLayer({
            id: TRAIL_LAYER,
            type: 'line',
            source: TRAIL_SOURCE,
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: {
              'line-color': '#000b29',
              'line-width': 4,
              'line-opacity': 0.85,
            },
          });
        }
        if (handlesRef.current && routeStore.value) {
          syncRouteToMap(maplibregl, handlesRef.current, routeStore.value);
        }
      });
      handlesRef.current = {
        map,
        traveller: null,
        destination: null,
        start: null,
        finish: null,
        // We already applied bounds in the constructor — skip syncRouteToMap's
        // fitBounds so the user's pan/zoom isn't overridden on the next frame.
        hasFitInitialBounds: true,
        lastFollowedWaypointKey: lastWaypointKey(routeStore.value),
        lastFittedRouteKey: routeCameraKey(routeStore.value),
      };
      setReady(true);
    });
    return () => {
      cancelled = true;
      handlesRef.current?.map.remove();
      handlesRef.current = null;
    };
  }, []);

  // Re-run on every route signal change once the map is ready. Also reacts
  // to playbackProgress so the trail slices/restores as playback animates.
  useSignalEffect(() => {
    const route = routeStore.value;
    const progress = playbackProgress.value;
    const handles = handlesRef.current;
    const maplibregl = moduleRef.current;
    if (!route || !handles || !maplibregl || !ready) return;
    const map = handles.map;
    if (!map.isStyleLoaded()) {
      map.once('load', () => syncRouteToMap(maplibregl, handles, route, progress, cameraMode, setCameraMode, userSelectedCameraModeRef));
      return;
    }
    syncRouteToMap(maplibregl, handles, route, progress, cameraMode, setCameraMode, userSelectedCameraModeRef);
  });

  useEffect(() => {
    const route = routeStore.value;
    const progress = playbackProgress.value;
    const handles = handlesRef.current;
    const maplibregl = moduleRef.current;
    if (!route || !handles || !maplibregl || !ready || !handles.map.isStyleLoaded()) return;
    syncRouteToMap(maplibregl, handles, route, progress, cameraMode, setCameraMode, userSelectedCameraModeRef, true);
  }, [cameraMode, ready]);

  const chooseCameraMode = (mode: CameraMode) => {
    userSelectedCameraModeRef.current = true;
    setCameraMode(mode);
  };

  // Outer div carries the absolute positioning that puts the map underneath
  // the floating UI. MapLibre takes the inner div as its container and sets
  // `position: relative` on it — explicit h-full/w-full keep it sized correctly
  // (relative + inset-0 would resolve to 0×0 since insets don't apply to a
  // relative element).
  return (
    <div class="absolute inset-0 bg-surface-muted">
      <div ref={containerRef} class="h-full w-full" aria-hidden="true" />
      <MapCameraControl mode={cameraMode} onChange={chooseCameraMode} />
    </div>
  );
}

// Guard against junk coordinates on the wire: a missing/NaN lat-lon, or the
// (0, 0) null-island fix the device emits before it has a real GPS lock. Either
// would otherwise yank the map trail to the Gulf of Guinea.
function validWaypoints(waypoints: RouteWaypoint[]): RouteWaypoint[] {
  return waypoints.filter(
    (w) =>
      Number.isFinite(w.lat) &&
      Number.isFinite(w.lon) &&
      !(w.lat === 0 && w.lon === 0),
  );
}

function validDestination(
  destination: RouteDestination | null | undefined,
): RouteDestination | null {
  if (!destination) return null;
  if (!Number.isFinite(destination.lat) || !Number.isFinite(destination.lng)) return null;
  if (destination.lat === 0 && destination.lng === 0) return null;
  return destination;
}

// Emergency-only stub routes (api #143) may carry no waypoints at all; the
// emergency subdoc's own location is then the only point worth showing.
function validEmergencyPoint(route: Route | null): { lon: number; lat: number } | null {
  const location = route?.emergency?.location;
  if (!location) return null;
  if (!Number.isFinite(location.lat) || !Number.isFinite(location.lng)) return null;
  if (location.lat === 0 && location.lng === 0) return null;
  return { lon: location.lng, lat: location.lat };
}

function syncRouteToMap(
  maplibregl: MapLibreModule,
  handles: MapHandles,
  route: Route,
  playback: number | null = null,
  cameraMode: CameraMode = 'route',
  setCameraMode?: (mode: CameraMode) => void,
  userSelectedCameraMode?: { current: boolean },
  forceCamera = false,
): void {
  const { map } = handles;
  const waypoints = validWaypoints(route.waypoints ?? []);
  const lastWaypoint = waypoints[waypoints.length - 1] ?? null;

  // During playback, slice the waypoint list to the corresponding prefix so
  // the trail "draws" from start to end. When playback is null we show the
  // full trail.
  const visibleWaypoints =
    playback === null
      ? waypoints
      : waypoints.slice(0, Math.max(2, Math.ceil(waypoints.length * playback)));

  const trailSource = map.getSource(TRAIL_SOURCE) as import('maplibre-gl').GeoJSONSource | undefined;
  if (trailSource) {
    trailSource.setData({
      type: 'FeatureCollection',
      features:
        visibleWaypoints.length > 0
          ? [
              {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates: visibleWaypoints.map((w) => [w.lon, w.lat]),
                },
              },
            ]
          : [],
    });
  }

  const destination = validDestination(route.destination);

  const emergencyActive = route.emergency?.state === 'active';
  // Show finish flag instead of pulsing traveller once the route is done.
  // Playback overrides — during replay we want the pulsing head to track the
  // animation, even on a finished route.
  const showFinishFlag = route.finished && playback === null;

  // First waypoint = start flag. Always shown when we have any waypoints.
  const firstWaypoint = waypoints[0] ?? null;
  if (firstWaypoint) {
    if (!handles.start) {
      const el = document.createElement('div');
      el.className = 'as-marker-start';
      el.setAttribute('aria-label', 'Start');
      // Offset pulls the flag a few px left so the pole base lines up with the
      // coordinate rather than the image's bottom-left bounding-box corner.
      handles.start = new maplibregl.Marker({ element: el, anchor: 'bottom-left', offset: [-6, 0] })
        .setLngLat([firstWaypoint.lon, firstWaypoint.lat])
        .addTo(map);
    } else {
      handles.start.setLngLat([firstWaypoint.lon, firstWaypoint.lat]);
    }
  }

  // During playback, the marker tracks the visible head so it looks like the
  // traveller moving along the route. Otherwise it sits at the latest
  // waypoint, falling back to the emergency location on waypoint-less stub
  // routes so the marker (and its red pulse) still has somewhere to stand.
  const emergencyPoint = validEmergencyPoint(route);
  const markerPoint =
    playback === null
      ? lastWaypoint ?? emergencyPoint
      : visibleWaypoints[visibleWaypoints.length - 1] ?? lastWaypoint ?? emergencyPoint;

  // Drop any stale markers whose underlying point is gone (e.g. the waypoint
  // list emptied out). Runs after the create blocks above, but the conditions
  // are mutually exclusive so a marker is never created and removed in one pass.
  if (!firstWaypoint && handles.start) {
    handles.start.remove();
    handles.start = null;
  }
  if (!markerPoint && handles.traveller) {
    handles.traveller.remove();
    handles.traveller = null;
  }
  if (!lastWaypoint && handles.finish) {
    handles.finish.remove();
    handles.finish = null;
  }

  if (showFinishFlag && lastWaypoint) {
    // Hide the pulsing traveller, show a checkered finish flag.
    if (handles.traveller) {
      handles.traveller.remove();
      handles.traveller = null;
    }
    if (!handles.finish) {
      const el = document.createElement('div');
      el.className = 'as-marker-finish';
      el.setAttribute('aria-label', 'Finish');
      handles.finish = new maplibregl.Marker({ element: el, anchor: 'bottom-left', offset: [-6, 0] })
        .setLngLat([lastWaypoint.lon, lastWaypoint.lat])
        .addTo(map);
    } else {
      handles.finish.setLngLat([lastWaypoint.lon, lastWaypoint.lat]);
    }
  } else if (markerPoint) {
    // Active route OR finished route in playback — show the pulsing traveller.
    if (handles.finish) {
      handles.finish.remove();
      handles.finish = null;
    }
    if (!handles.traveller) {
      const el = document.createElement('div');
      el.className = `as-marker-traveller${emergencyActive ? ' as-marker-emergency' : ''}`;
      el.setAttribute('aria-label', 'Traveller position');
      handles.traveller = new maplibregl.Marker({ element: el })
        .setLngLat([markerPoint.lon, markerPoint.lat])
        .addTo(map);
    } else {
      handles.traveller.setLngLat([markerPoint.lon, markerPoint.lat]);
      // Toggle the emergency class on the existing element so the colour
      // flips immediately when the API socket pushes the emergency event.
      const el = handles.traveller.getElement();
      el.classList.toggle('as-marker-emergency', emergencyActive);
    }
  }

  if (destination) {
    if (!handles.destination) {
      const el = document.createElement('div');
      el.className = 'as-marker-destination';
      el.setAttribute('aria-label', 'Destination');
      handles.destination = new maplibregl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([destination.lng, destination.lat])
        .addTo(map);
    } else {
      handles.destination.setLngLat([destination.lng, destination.lat]);
    }
  } else if (handles.destination) {
    handles.destination.remove();
    handles.destination = null;
  }

  if (!handles.hasFitInitialBounds) {
    const bounds = new maplibregl.LngLatBounds();
    for (const w of waypoints) bounds.extend([w.lon, w.lat]);
    if (destination) bounds.extend([destination.lng, destination.lat]);
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, {
        padding: INITIAL_VIEW_PADDING,
        maxZoom: INITIAL_VIEW_MAX_ZOOM,
        duration: 0,
      });
      handles.hasFitInitialBounds = true;
    }
  }

  syncCameraToMode(maplibregl, handles, route, markerPoint, route.finished === true, playback, cameraMode, setCameraMode, userSelectedCameraMode, forceCamera);
}

// Padding tuned so the route doesn't crowd the floating UI: top clears
// the StatusHero card, bottom clears the BottomSheet handle, sides leave
// breathing room from the device edges.
const INITIAL_VIEW_PADDING = { top: 160, right: 56, bottom: 260, left: 56 };
const INITIAL_VIEW_MAX_ZOOM = 15;
const LIVE_FOLLOW_ZOOM = 15;
const LIVE_FOLLOW_OFFSET: [number, number] = [0, -80];

function routeCameraKey(route: Route | null): string | null {
  if (!route) return null;
  const waypoints = route.waypoints ?? [];
  const first = waypoints[0];
  const last = waypoints[waypoints.length - 1];
  const destination = route.destination;
  return [
    waypoints.length,
    first ? waypointCameraKey(first) : '',
    last ? waypointCameraKey(last) : '',
    destination ? `${destination.lat}|${destination.lng}` : '',
  ].join('::');
}

function waypointCameraKey(waypoint: { lat: number; lon: number; time?: string | Date; odo?: number }): string {
  return [
    waypoint.time instanceof Date ? waypoint.time.toISOString() : waypoint.time ?? '',
    waypoint.lat,
    waypoint.lon,
    waypoint.odo ?? '',
  ].join('|');
}

function lastWaypointKey(route: Route | null): string | null {
  const waypoints = route?.waypoints ?? [];
  const last = waypoints[waypoints.length - 1];
  return last ? waypointCameraKey(last) : null;
}

function followLatestWaypoint(
  handles: MapHandles,
  waypoint: Route['waypoints'][number] | null,
  finished: boolean,
  playback: number | null,
  force = false,
): void {
  if (!waypoint || finished || playback !== null) return;

  const key = waypointCameraKey(waypoint);
  if (!force && handles.lastFollowedWaypointKey === key) return;
  handles.lastFollowedWaypointKey = key;

  const map = handles.map;
  map.easeTo({
    center: [waypoint.lon, waypoint.lat],
    zoom: Math.max(map.getZoom(), LIVE_FOLLOW_ZOOM),
    offset: LIVE_FOLLOW_OFFSET,
    duration: 700,
    essential: true,
  });
}

function fitWholeRoute(
  maplibregl: MapLibreModule,
  handles: MapHandles,
  route: Route,
  force = false,
): void {
  const key = routeCameraKey(route);
  if (!force && handles.lastFittedRouteKey === key) return;
  handles.lastFittedRouteKey = key;

  const bounds = new maplibregl.LngLatBounds();
  for (const w of validWaypoints(route.waypoints ?? [])) bounds.extend([w.lon, w.lat]);
  const destination = validDestination(route.destination);
  if (destination) bounds.extend([destination.lng, destination.lat]);
  if (bounds.isEmpty()) return;

  handles.map.fitBounds(bounds, {
    padding: INITIAL_VIEW_PADDING,
    maxZoom: INITIAL_VIEW_MAX_ZOOM,
    duration: force ? 450 : 700,
    essential: true,
  });
}

function syncCameraToMode(
  maplibregl: MapLibreModule,
  handles: MapHandles,
  route: Route,
  latestWaypoint: Route['waypoints'][number] | null,
  finished: boolean,
  playback: number | null,
  cameraMode: CameraMode,
  setCameraMode?: (mode: CameraMode) => void,
  userSelectedCameraMode?: { current: boolean },
  force = false,
): void {
  if (playback !== null) return;

  const latestKey = latestWaypoint ? waypointCameraKey(latestWaypoint) : null;
  const hasNewLiveWaypoint =
    latestKey !== null &&
    handles.lastFollowedWaypointKey !== null &&
    handles.lastFollowedWaypointKey !== latestKey;

  if (
    cameraMode === 'route' &&
    hasNewLiveWaypoint &&
    userSelectedCameraMode?.current !== true &&
    !finished
  ) {
    setCameraMode?.('current');
    followLatestWaypoint(handles, latestWaypoint, finished, playback, true);
    return;
  }

  if (cameraMode === 'route') {
    fitWholeRoute(maplibregl, handles, route, force);
    return;
  }

  if (cameraMode === 'current') {
    followLatestWaypoint(handles, latestWaypoint, finished, playback, force);
  }
}

function MapCameraControl({
  mode,
  onChange,
}: {
  mode: CameraMode;
  onChange: (mode: CameraMode) => void;
}) {
  const options: Array<{ mode: CameraMode; label: string; Icon: typeof RouteIcon }> = [
    { mode: 'route', label: 'Keep whole route in view', Icon: RouteIcon },
    { mode: 'current', label: 'Follow current location', Icon: LocateFixed },
    { mode: 'free', label: 'Free pan', Icon: Hand },
  ];

  return (
    <div
      class="pointer-events-auto fixed right-3 top-28 z-20 inline-flex flex-col overflow-hidden rounded-full border border-border/70 bg-surface/95 shadow-md backdrop-blur-sm"
      role="group"
      aria-label="Map camera mode"
    >
      {options.map(({ mode: optionMode, label, Icon }) => {
        const active = mode === optionMode;
        return (
          <button
            key={optionMode}
            type="button"
            onClick={() => onChange(optionMode)}
            class={[
              'flex h-10 w-10 items-center justify-center transition-colors',
              active ? 'bg-primary text-white' : 'text-primary hover:bg-surface-muted',
            ].join(' ')}
            aria-label={label}
            aria-pressed={active}
            title={label}
          >
            <Icon size={17} aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}

/**
 * Compute the view (centre+zoom or bounds) we want the map to open at. The
 * goal is "the first rendered frame is the final frame" — no fitBounds
 * jump on the next paint.
 */
type InitialView = {
  center?: [number, number];
  zoom?: number;
  bounds?: import('maplibre-gl').LngLatBoundsLike;
  fitBoundsOptions?: import('maplibre-gl').FitBoundsOptions;
};

function computeInitialView(maplibregl: MapLibreModule, route: Route | null): InitialView {
  const waypoints = route?.waypoints ?? [];
  const destination = route?.destination;
  if (waypoints.length === 0 && !destination) {
    // Reachable on emergency-only stub routes (api #143) — App.tsx lets
    // them through the boot gate without waypoints. Centre on the
    // emergency location so the map opens where the banner points.
    const emergencyPoint = validEmergencyPoint(route);
    if (emergencyPoint) {
      return { center: [emergencyPoint.lon, emergencyPoint.lat], zoom: 15 };
    }
    // Otherwise degrade gracefully to Germany centroid rather than throwing.
    return { center: [10.4515, 51.1657], zoom: 5 };
  }
  if (waypoints.length === 1 && !destination) {
    // Single point — there's no bounds shape; centre on it at city zoom.
    const w = waypoints[0]!;
    return { center: [w.lon, w.lat], zoom: 14 };
  }
  const bounds = new maplibregl.LngLatBounds();
  for (const w of waypoints) bounds.extend([w.lon, w.lat]);
  if (destination) bounds.extend([destination.lng, destination.lat]);
  return {
    bounds,
    fitBoundsOptions: {
      padding: INITIAL_VIEW_PADDING,
      maxZoom: INITIAL_VIEW_MAX_ZOOM,
    },
  };
}
